/** Shared portal sidebar menu filtering (partner / workspace / executive). */

export function pathMatchesNavItem(pathname, to) {
  if (!to || to === "#") return false;
  const path = pathname.replace(/\/$/, "");
  const base = to.replace(/\/$/, "");
  if (path === base) return true;
  return path.startsWith(`${base}/`);
}

export function navItemIsActive(pathname, item) {
  if (!item?.to) return false;
  const paths = item.activeMatchPaths?.length ? item.activeMatchPaths : [item.to];
  return paths.some((p) => pathMatchesNavItem(pathname, p));
}

const FRANCHISE_ADMIN_PERMISSIONS = new Set([
  "view_dashboard",
  "view_complaints",
  "view_store_kpis",
]);

function hqAdminHasPermission(permissionService, key) {
  switch (key) {
    case "manage_products":
      return permissionService.canManageProducts();
    case "manage_services":
      return permissionService.canManageServices();
    case "view_org_services_readonly":
      return permissionService.canViewOrgServicesReadonly?.() ?? false;
    case "view_dashboard":
      return permissionService.canViewDashboard();
    case "view_store_kpis":
      return permissionService.canViewStoreKpis();
    case "view_complaints":
      return permissionService.canViewComplaints();
    case "view_contact_messages":
      return permissionService.canViewContactMessages();
    case "company_profile":
      return permissionService.canEditCompanyProfile();
    case "edit_set_flow":
      return permissionService.canEditSetFlow();
    case "manage_kiosk_landings":
      return permissionService.canManageKioskLandings();
    case "manage_skin_settings":
      return permissionService.canManageSkinSettings();
    default:
      return true;
  }
}

export function filterPortalMenuItems(items, permissionService, options = {}) {
  const { dashboardOnly = false, dashboardOnlyKeys = [] } = options;

  return (items || []).filter((item) => {
    if (dashboardOnly) {
      return dashboardOnlyKeys.includes(item.key);
    }

    if (item.adminOnly && !permissionService.isAdmin()) return false;
    if (item.superAdminOnly && !permissionService.isSuperAdmin()) return false;
    if (item.hideForSuperAdmin && permissionService.isSuperAdmin()) return false;
    if (item.requiresOrganizationScope) {
      if (permissionService.isSuperAdmin()) return false;
      if (!permissionService.isOrganizationScope?.()) return false;
    }
    if (item.regionalReadOnlyCatalog && permissionService.isOrganizationScope?.()) {
      if (permissionService.canManageServices?.()) return false;
    }
    if (item.franchiseCreateOnly && !permissionService.canCreateFranchiseUsers()) return false;
    if (
      item.regionScopeAccess &&
      !(permissionService.isSuperAdmin() || permissionService.canCreateFranchiseUsers())
    ) {
      return false;
    }
    if (item.requiresWhatsapp && !permissionService.canUseWhatsappCredentials()) return false;

    if (item.userOnly && permissionService.isAdmin()) {
      if (!(item.allowSuperAdmin && permissionService.isSuperAdmin())) return false;
    }

    if (permissionService.isFranchise()) {
      if (Array.isArray(item.adminPermissionAny) && item.adminPermissionAny.length) return false;
      if (item.adminPermission) return FRANCHISE_ADMIN_PERMISSIONS.has(item.adminPermission);
    }

    if (Array.isArray(item.adminPermissionAny) && item.adminPermissionAny.length) {
      if (!item.adminPermissionAny.some((key) => hqAdminHasPermission(permissionService, key))) {
        return false;
      }
    } else if (item.adminPermission && !hqAdminHasPermission(permissionService, item.adminPermission)) {
      return false;
    }

    if (!item.permission) return true;
    if (Array.isArray(item.permission)) return permissionService.hasAny(...item.permission);
    return permissionService.has(item.permission);
  });
}
